import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { MyConfig, MyConfigOrganization, MyConfigOrganizationConfigSubOrganization } from '../models/myConfig';
import { ParkingService } from './parking.service';
import { BehaviorSubject } from 'rxjs';
import { AppEvent, AppEventType, EventQueueService } from './eventqueue.service';

@Injectable({
  providedIn: 'root'
})

export class MyConfigService {
  private hostPath: string = JSON.parse(environment.IDENTITY);
  public myConfig: MyConfig;

  private configSubject = new BehaviorSubject<MyConfig | null>(null);
  config$ = this.configSubject.asObservable();

  constructor(
    private http: HttpClient,
    private parkingService: ParkingService,
    public eventQueue: EventQueueService) {
    const stored = this.getMyConfig();
    if (stored) {
      this.configSubject.next(stored);
    }
  }


  public setMyConfig(myConfig: MyConfig) {
    this.myConfig = myConfig;
    localStorage.setItem('MyConfig', JSON.stringify(myConfig));
    this.configSubject.next(myConfig);
  }

  public getMyConfig(): MyConfig {
    if (!this.myConfig && localStorage.getItem('MyConfig') !== null) {
      this.myConfig = JSON.parse(localStorage.getItem('MyConfig'));
    }
    return this.myConfig;
  }

  public getMyConfigOrganizationById(organizationId: string): MyConfigOrganization {
    const config = this.getMyConfig();
    if (!config || !config.organizations) return null;

    return config.organizations.find(org => org.organizationId === organizationId);
  }

  public removeMyConfig() {
    this.myConfig = null;
    if (localStorage.getItem('MyConfig') != null) {
      localStorage.removeItem('MyConfig');
    }
  }

  getAllMyConfigOrganizations(): MyConfigOrganizationConfigSubOrganization[] {
    var result: MyConfigOrganizationConfigSubOrganization[] = [];
    this.getMyConfig().organizations.forEach((organization: MyConfigOrganization) => {
      result.push({
        organizationId: organization.organizationId,
        name: organization.organizationName,
        typeName: organization.organizationType
      });

      organization.config.subOrganizations.forEach((subOrganization: MyConfigOrganizationConfigSubOrganization) => {
        result.push(subOrganization);
      });
    });
    return result;
  }

  public async fetchAndSetMyConfig(attempt: number = 1, maxAttempts: number = 3, retryDelay: number = 1000): Promise<MyConfig | null> {
    try {
      const url = this.hostPath + 'Shuttlebuss/Shuttlebuss.Service/my/config';
      const config = await this.http.get<MyConfig>(url).toPromise();

      if (config) {
        this.setMyConfig(config);
        this.eventQueue.dispatch(new AppEvent(AppEventType.MyConfigLoaded, true));
        return this.getMyConfig();
      }

      return null;
    } catch (error) {
      console.error(`Error fetching MyConfig (attempt ${attempt}/${maxAttempts}):`, error);

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.fetchAndSetMyConfig(attempt + 1, maxAttempts, retryDelay);
      } else {
        console.error('Max retry attempts reached. Logging out...');
        return null;
      }
    }
  }

  // public async loadParkingDataForOrganizations(organizations: MyConfigOrganization[]) {
  //   if (!organizations || organizations.length === 0) return;

  //   try {
  //     let currentConfig = this.getMyConfig() || { organizations: [] };

  //     for (const org of organizations) {
  //       currentConfig = await this.loadParkingDataForOrganization(org.organizationId, currentConfig);
  //     }

  //     this.setMyConfig(currentConfig);
  //     return currentConfig;
  //   } catch (error) {
  //     console.error('Error loading parking data for organizations:', error);
  //   }
  // }

  // public async loadParkingDataForOrganization(organizationId: string, myConfig: MyConfig) {
  //   try {
  //     const configCopy = JSON.parse(JSON.stringify(myConfig));
  //     const parkings = await this.parkingService.getParkingsOrganization(organizationId).toPromise();

  //     // console.log('MyConfigService: loadParkingDataForOrganization:', organizationId, parkings);

  //     if (parkings && parkings.length > 0) {
  //       const orgIndex = configCopy.organizations.findIndex(o => o.organizationId === organizationId);

  //       if (orgIndex !== -1) {
  //         if (!configCopy.organizations[orgIndex].config) {
  //           configCopy.organizations[orgIndex].config = {};
  //         }

  //         if (!configCopy.organizations[orgIndex].config.parkings) {
  //           configCopy.organizations[orgIndex].config.parkings = [];
  //         }

  //         configCopy.organizations[orgIndex].config.parkings = parkings.map(p => ({
  //           organizationId: organizationId,
  //           parkingId: p.parkingId,
  //           parkingName: p.parkingName,
  //           parkingDescription: p.parkingDescription
  //         }));
  //       }
  //     }

  //     return configCopy;
  //   } catch (error) {
  //     console.error(`Error loading parking data for organization ${organizationId}:`, error);
  //     return myConfig;
  //   }
  // }

  public hasOrganizationRole(organizationName: string, roleName: string): boolean {
    if (!this.myConfig?.organizations) return false;

    const organization = this.myConfig.organizations.find(org => org.organizationName === organizationName);
    if (!organization) return false;

    return organization.organizationMemberType.indexOf(roleName) > -1;
  }


  public hasOrganizationIdRole(organizationId: string, roleName: string, organizationParentId?: string): boolean {
    if (!this.myConfig?.organizations) return false;

    var organization = this.myConfig.organizations.find(org => org.organizationId === organizationId);

    if (!organization) {
      if (organizationParentId != null) {
        organization = this.myConfig.organizations.find(org => org.organizationId === organizationParentId);
        if (!organization) return false;
      }
      else return false;
    }

    return organization.organizationMemberType.indexOf(roleName) > -1;
  }

  public getOrganizationsWithRole(roleName: string): MyConfigOrganization[] {
    if (!this.myConfig?.organizations) return [];

    return this.myConfig.organizations.filter(org =>
      org.organizationMemberType.indexOf(roleName) > -1
    );
  }

  public hasModuleAccess(moduleName: string, organization: MyConfigOrganization, admin: boolean): boolean {
    let result = false;
    for (let module of organization.config.modules) {
      if (module.name === moduleName) {
        if (admin) {
          result = true;
          return result;
        }
        let moduleRoles: string[] = new Array<string>();
        if (module.roles.indexOf(',') > -1) {
          moduleRoles = module.roles.replace(' ', '').split(',');
        }
        else {
          moduleRoles.push(module.roles.replace(' ', ''));
        }

        let organizationRoles: string[] = new Array<string>();
        if (organization.organizationMemberType.indexOf(',') > -1) {
          organizationRoles = organization.organizationMemberType.replace(' ', '').split(',')
        }
        else {
          organizationRoles.push(organization.organizationMemberType.replace(' ', ''));
        }

        moduleRoles.forEach((moduleRole: string) => {
          organizationRoles.forEach((organizationRole: string) => {
            if (moduleRole.toString().trim() == organizationRole.toString().trim()) {
              result = true;
            };
          });
        });
      }
    }
    return result;
  }

  hasBackOfficeAccess(): boolean {
    var showBackOffice = false;
    this.getMyConfig().organizations.forEach((organization: MyConfigOrganization) => {
      if (organization.config.backOfficeModules && organization.config.backOfficeModules.length > 0) {
        showBackOffice = true;
      }
    });
    return showBackOffice;
  }

}
